import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  PiggyBank,
  BarChart3,
  ArrowUpRight,
  Calculator,
  ArrowLeft,
} from "lucide-react";
import { Button } from "@/common/components/ui/button";
import { Label } from "@/common/components/ui/label";
import { Card, CardContent } from "@/common/components/ui/card";
import { CompactCalculatorRow } from "../CompactCalculatorRow";
import { calculateFD, formatCurrency } from "../../utils/calculations";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import ProductLayout from "@/common/components/ProductLayout";

export const FDCalculator = () => {
  const navigate = useNavigate();
  const [fdAmount, setFdAmount] = useState(100000);
  const [fdRate, setFdRate] = useState(7.5);
  const [fdTenure, setFdTenure] = useState(12);
  const [fdCompounding, setFdCompounding] = useState<
    "quarterly" | "monthly" | "yearly"
  >("quarterly");

  const fdResult = useMemo(
    () => calculateFD(fdAmount, fdRate, fdTenure, fdCompounding),
    [fdAmount, fdRate, fdTenure, fdCompounding]
  );

  const fdPieData = [
    { name: "Principal", value: fdAmount, color: "#14b8a6" },
    { name: "Interest", value: fdResult.interest, color: "#3b82f6" },
  ];

  return (
    <ProductLayout>
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate("/calculators")}
              className="mb-4 pl-0 hover:bg-transparent hover:text-primary"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Calculators
            </Button>
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-primary/10 p-3 text-primary">
                <Calculator className="h-8 w-8" />
              </div>
              <h1 className="text-3xl font-bold text-secondary">
                FD Calculator
              </h1>
            </div>
          </div>

          <div className="mx-auto max-w-7xl">
            <div className="grid gap-10 lg:grid-cols-2">
              <Card className="border-border bg-card">
                <CardContent className="space-y-6 p-6 md:p-8">
                  <div className="mb-2 flex items-center gap-3">
                    <div className="rounded-xl bg-primary/10 p-3">
                      <PiggyBank className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-secondary">
                        FD Calculator
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Calculate your maturity amount
                      </p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <CompactCalculatorRow
                      label="Total investment"
                      value={fdAmount}
                      onChange={setFdAmount}
                      min={500}
                      max={10000000}
                      step={500}
                      prefix="₹"
                      limitLabels={["₹500", "₹1 Cr"]}
                      colorClass="text-primary"
                    />

                    <CompactCalculatorRow
                      label="Interest Rate (p.a)"
                      value={fdRate}
                      onChange={setFdRate}
                      min={1}
                      max={20}
                      step={0.1}
                      suffix="%"
                      colorClass="text-green-600"
                    />

                    <CompactCalculatorRow
                      label="Time period"
                      value={fdTenure}
                      onChange={setFdTenure}
                      min={1}
                      max={300}
                      step={1}
                      suffix="Mo"
                      limitLabels={["1 Mo", "25 Yrs"]}
                      colorClass="text-purple-600"
                    />

                    <div className="space-y-3">
                      <Label className="font-medium text-secondary">
                        Compounding Frequency
                      </Label>
                      <div className="grid grid-cols-3 gap-2">
                        {(["monthly", "quarterly", "yearly"] as const).map(
                          type => (
                            <button
                              key={type}
                              onClick={() => setFdCompounding(type)}
                              className={`rounded-xl px-4 py-3 text-sm font-semibold transition-all ${
                                fdCompounding === type
                                  ? "bg-primary text-white shadow-md"
                                  : "bg-muted/50 text-muted-foreground hover:bg-muted"
                              }`}
                            >
                              {type.charAt(0).toUpperCase() + type.slice(1)}
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-6">
                <Card className="border-border bg-muted/20">
                  <CardContent className="p-6 md:p-8">
                    <h3 className="mb-6 flex items-center gap-2 text-lg font-bold text-secondary">
                      <BarChart3 className="h-5 w-5 text-primary" />
                      Maturity Summary
                    </h3>

                    <div className="mb-6 grid grid-cols-2 gap-4">
                      <div className="h-[180px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={fdPieData}
                              cx="50%"
                              cy="50%"
                              innerRadius={45}
                              outerRadius={70}
                              paddingAngle={3}
                              dataKey="value"
                            >
                              {fdPieData.map(entry => (
                                <Cell key={entry.name} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip
                              formatter={(value: number | undefined) =>
                                value !== undefined ? formatCurrency(value) : ""
                              }
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex flex-col justify-center space-y-3">
                        {fdPieData.map(item => (
                          <div
                            key={item.name}
                            className="flex items-center gap-2"
                          >
                            <div
                              className="h-3 w-3 rounded-full"
                              style={{ backgroundColor: item.color }}
                            />
                            <span className="text-sm text-muted-foreground">
                              {item.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between rounded-xl bg-white p-4 shadow-sm">
                        <span className="text-muted-foreground">
                          Principal Amount
                        </span>
                        <span className="text-xl font-bold text-secondary">
                          {formatCurrency(fdAmount)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between rounded-xl bg-white p-4 shadow-sm">
                        <span className="flex items-center gap-2 text-muted-foreground">
                          <ArrowUpRight className="h-4 w-4 text-green-500" />
                          Interest Earned
                        </span>
                        <span className="text-xl font-bold text-green-600">
                          {formatCurrency(fdResult.interest)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between rounded-xl bg-gradient-to-r from-primary to-accent p-5 text-white shadow-lg">
                        <span className="font-semibold">
                          Total Maturity Value
                        </span>
                        <span className="text-2xl font-bold">
                          {formatCurrency(fdResult.maturity)}
                        </span>
                      </div>
                    </div>

                    <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
                      <p className="text-sm text-amber-700">
                        *Calculations are indicative. Actual returns may vary
                        based on bank policies and tax deductions.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ProductLayout>
  );
};
